import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../base';
import { handleAnalyticsEvent, hasUserConsented } from '../helpers/analyticsHandler';
import './PurchaseFeedback.css';

const REASONS = ['price', 'not_ready', 'trial_first', 'unclear_value', 'technical_issue', 'other'];

export default function PurchaseFeedback({ placement = 'pricing' }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('idle');
  const signedIn = Boolean(auth.currentUser && !auth.currentUser.email?.startsWith('guest_'));

  // No anonymous Firestore writes; guests can submit only if analytics consent is active.
  if (!signedIn && (typeof document === 'undefined' || !hasUserConsented())) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!REASONS.includes(reason) || status === 'saving' || status === 'done') return;
    setStatus('saving');
    try {
      const user = auth.currentUser;
      if (user && !user.email?.startsWith('guest_')) {
        await addDoc(collection(db, 'purchaseFeedback', user.uid, 'responses'), {
          reason,
          details: details.trim().slice(0, 500),
          placement,
          plan: 'my_project_plan',
          createdAt: serverTimestamp(),
        });
      }
      // Anonymous visitors only contribute a consent-gated, non-identifying event.
      handleAnalyticsEvent('purchase_feedback', placement, reason);
      setStatus('done');
    } catch (error) {
      console.error('Could not save purchase feedback:', error);
      setStatus('error');
    }
  };

  return (
    <section className="purchase-feedback" aria-label={t('feedback.title', { defaultValue: 'Help us improve My Project' })}>
      <h3>{t('feedback.title', { defaultValue: 'Help us improve My Project' })}</h3>
      <p>{t('feedback.question', { defaultValue: 'What is holding you back from buying the one-project package?' })}</p>
      {status === 'done' ? <p role="status">{t('feedback.thanks', { defaultValue: 'Thanks for telling us.' })}</p> : (
        <form onSubmit={submit}>
          <label htmlFor={`purchase-reason-${placement}`}>{t('feedback.reason', { defaultValue: 'Main reason' })}</label>
          <select id={`purchase-reason-${placement}`} value={reason} onChange={(event) => setReason(event.target.value)} required>
            <option value="">{t('feedback.choose', { defaultValue: 'Choose a reason' })}</option>
            {REASONS.map((item) => <option key={item} value={item}>{t(`feedback.${item}`, {
              defaultValue: {
                price: 'Price or payment concerns',
                not_ready: 'I am not ready to start',
                trial_first: 'I want to try it first',
                unclear_value: 'I am unsure what is included',
                technical_issue: 'Something did not work',
                other: 'Something else',
              }[item],
            })}</option>)}
          </select>
          <label htmlFor={`purchase-details-${placement}`}>{t('feedback.details', { defaultValue: 'Anything else? (optional)' })}</label>
          <textarea id={`purchase-details-${placement}`} value={details} maxLength={500} onChange={(event) => setDetails(event.target.value)} rows={2} />
          <button type="submit" disabled={!reason || status === 'saving'}>{t('feedback.send', { defaultValue: 'Send feedback' })}</button>
          {status === 'error' && <p role="alert">{t('feedback.error', { defaultValue: 'Could not send feedback. Please try again.' })}</p>}
        </form>
      )}
    </section>
  );
}
